import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LanguageSwitcher } from './LanguageSwitcher';
import { DarkModeToggle } from './DarkModeToggle';
import { Footer } from './Footer';
import { useSharedQuote, useRespondToQuote } from '@/api/hooks';

interface SharedQuotePageProps {
  token: string;
}

function formatCurrency(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
}

function addDays(dateStr: string, days: number): Date {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date;
}

function formatDate(dateStr: string, locale: string): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="animate-pulse bg-muted rounded h-8 w-3/4" />
      <div className="animate-pulse bg-muted rounded h-40 w-full" />
      <div className="animate-pulse bg-muted rounded h-24 w-full" />
      <div className="animate-pulse bg-muted rounded h-32 w-full" />
    </div>
  );
}

export function SharedQuotePage({ token }: Readonly<SharedQuotePageProps>) {
  const { t, i18n } = useTranslation();
  const sharedQuote = useSharedQuote(token);
  const respondToQuote = useRespondToQuote();

  const [clientName, setClientName] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const locale = i18n.language === 'et' ? 'et-EE' : 'en-US';

  const handleRespond = (response: 'accepted' | 'rejected') => {
    respondToQuote.mutate(
      {
        token,
        name: clientName.trim(),
        message,
        response,
        _hp: honeypot,
      },
      {
        onSuccess: () => {
          toast.success(t('sharedQuote.responseRecorded'));
          setSubmitted(true);
        },
      },
    );
  };

  const isNameEmpty = clientName.trim() === '';
  const buttonsDisabled = isNameEmpty || respondToQuote.isPending;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-[28rem] lg:max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">{t('app.title')}</span>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <DarkModeToggle />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-[28rem] lg:max-w-3xl mx-auto px-4 py-6 w-full">
        {sharedQuote.isLoading && <LoadingSkeleton />}

        {sharedQuote.isError && (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">{t('sharedQuote.invalidLink')}</p>
            </CardContent>
          </Card>
        )}

        {sharedQuote.data && !sharedQuote.isError && (
          <QuoteContent
            data={sharedQuote.data}
            clientName={clientName}
            message={message}
            honeypot={honeypot}
            onClientNameChange={setClientName}
            onMessageChange={setMessage}
            onHoneypotChange={setHoneypot}
            onRespond={handleRespond}
            buttonsDisabled={buttonsDisabled}
            submitted={submitted}
            locale={locale}
            t={t}
            i18n={i18n}
          />
        )}
      </main>

      <Footer />
      <Toaster />
    </div>
  );
}

interface QuoteContentProps {
  data: import('@/api/types').SharedQuoteView;
  clientName: string;
  message: string;
  honeypot: string;
  onClientNameChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onHoneypotChange: (value: string) => void;
  onRespond: (response: 'accepted' | 'rejected') => void;
  buttonsDisabled: boolean;
  submitted: boolean;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  i18n: { language: string };
}

function QuoteContent({
  data,
  clientName,
  message,
  honeypot,
  onClientNameChange,
  onMessageChange,
  onHoneypotChange,
  onRespond,
  buttonsDisabled,
  submitted,
  locale,
  t,
  i18n,
}: Readonly<QuoteContentProps>) {
  const { contractor, quote, client_response } = data;

  const subtotal = quote.grand_total;
  const markupAmount = subtotal * (quote.markup_percent / 100);
  const subtotalWithMarkup = subtotal + markupAmount;
  const isVatRegistered = contractor.vat_number.trim() !== '';
  const vatAmount = isVatRegistered ? subtotalWithMarkup * (quote.vat_rate / 100) : 0;
  const grandTotal = subtotalWithMarkup + vatAmount;

  const validUntilDate = addDays(quote.created_at, quote.validity_days);

  return (
    <div className="space-y-6">
      {/* Title */}
      <h1 className="text-2xl font-bold text-foreground">
        {t('sharedQuote.title', { company: contractor.company_name })}
      </h1>

      {/* Quote details card */}
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Date info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div>
              <span className="font-medium text-muted-foreground">
                {t('sharedQuote.quoteDate')}
              </span>
              <p className="text-foreground">{formatDate(quote.created_at, locale)}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">
                {t('sharedQuote.validUntil')}
              </span>
              <p className="text-foreground">{formatDate(validUntilDate.toISOString(), locale)}</p>
            </div>
            <div>
              <p className="text-foreground">
                {t('sharedQuote.paymentTerms', { days: quote.payment_term_days })}
              </p>
            </div>
          </div>

          {/* Client info */}
          {quote.client_name && (
            <div className="text-sm space-y-1">
              <div>
                <span className="font-medium text-muted-foreground">
                  {t('sharedQuote.clientName')}
                </span>
                <p className="text-foreground">{quote.client_name}</p>
              </div>
              {quote.client_address && (
                <div>
                  <span className="font-medium text-muted-foreground">
                    {t('sharedQuote.clientAddress')}
                  </span>
                  <p className="text-foreground">{quote.client_address}</p>
                </div>
              )}
            </div>
          )}

          {/* Items table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 font-medium text-muted-foreground">
                    {t('sharedQuote.itemDescription')}
                  </th>
                  <th className="text-right py-2 font-medium text-muted-foreground w-16">
                    {t('sharedQuote.itemQuantity')}
                  </th>
                  <th className="text-right py-2 font-medium text-muted-foreground w-24">
                    {t('sharedQuote.itemUnitPrice')}
                  </th>
                  <th className="text-right py-2 font-medium text-muted-foreground w-24">
                    {t('sharedQuote.itemTotal')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item) => (
                  <tr key={item.description} className="border-b border-border/50">
                    <td className="py-2 text-foreground">{item.description}</td>
                    <td className="py-2 text-right text-foreground">{item.quantity}</td>
                    <td className="py-2 text-right text-foreground">
                      {formatCurrency(item.unit_price, locale)}
                    </td>
                    <td className="py-2 text-right text-foreground">
                      {formatCurrency(item.total, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="space-y-1 text-sm">
            {(quote.markup_percent > 0 || isVatRegistered) && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sharedQuote.subtotal')}</span>
                <span className="text-foreground">{formatCurrency(subtotal, locale)}</span>
              </div>
            )}
            {quote.markup_percent > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('sharedQuote.markup', { percent: quote.markup_percent })}
                </span>
                <span className="text-foreground">{formatCurrency(markupAmount, locale)}</span>
              </div>
            )}
            {isVatRegistered && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('sharedQuote.vat', { percent: quote.vat_rate })}
                </span>
                <span className="text-foreground">{formatCurrency(vatAmount, locale)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-2 font-bold">
              <span>{t('sharedQuote.grandTotal')}</span>
              <span className="text-primary">{formatCurrency(grandTotal, locale)}</span>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className="text-sm">
              <span className="font-medium text-muted-foreground">{t('sharedQuote.notes')}</span>
              <p className="text-foreground mt-1 whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice section */}
      {data.invoice && (
        <Card>
          <CardHeader>
            <CardTitle>{t('invoice.invoiceInfo')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoice.number')}</span>
                <span>{data.invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoice.amount')}</span>
                <span>
                  {new Intl.NumberFormat(i18n.language === 'et' ? 'et-EE' : 'en-US', {
                    style: 'currency',
                    currency: 'EUR',
                  }).format(data.invoice.amount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('invoice.due')}</span>
                <span>
                  {new Date(data.invoice.due_date).toLocaleDateString(
                    i18n.language === 'et' ? 'et-EE' : 'en-US',
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('invoice.status')}</span>
                {data.invoice.payment_status === 'paid' ? (
                  <Badge variant="default" className="bg-green-600">
                    {t('invoice.paid')}
                  </Badge>
                ) : (
                  <Badge variant="secondary">{t('invoice.unpaid')}</Badge>
                )}
              </div>
              {data.invoice.payment_status === 'paid' && data.invoice.paid_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('invoice.paidAt')}</span>
                  <span>
                    {new Date(data.invoice.paid_at).toLocaleDateString(
                      i18n.language === 'et' ? 'et-EE' : 'en-US',
                    )}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response section */}
      <ResponseSection
        clientResponse={client_response}
        submitted={submitted}
        clientName={clientName}
        message={message}
        honeypot={honeypot}
        onClientNameChange={onClientNameChange}
        onMessageChange={onMessageChange}
        onHoneypotChange={onHoneypotChange}
        onRespond={onRespond}
        buttonsDisabled={buttonsDisabled}
        locale={locale}
        t={t}
      />
    </div>
  );
}

interface ResponseSectionProps {
  clientResponse: import('@/api/types').ClientResponse | null;
  submitted: boolean;
  clientName: string;
  message: string;
  honeypot: string;
  onClientNameChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onHoneypotChange: (value: string) => void;
  onRespond: (response: 'accepted' | 'rejected') => void;
  buttonsDisabled: boolean;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ResponseSection({
  clientResponse,
  submitted,
  clientName,
  message,
  honeypot,
  onClientNameChange,
  onMessageChange,
  onHoneypotChange,
  onRespond,
  buttonsDisabled,
  locale,
  t,
}: Readonly<ResponseSectionProps>) {
  if (clientResponse) {
    return <AlreadyRespondedCard response={clientResponse} locale={locale} t={t} />;
  }

  if (submitted) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Check className="h-12 w-12 text-green-600 mx-auto mb-3" />
          <p className="text-lg font-semibold text-foreground">{t('sharedQuote.thankYou')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <ResponseForm
      clientName={clientName}
      message={message}
      honeypot={honeypot}
      onClientNameChange={onClientNameChange}
      onMessageChange={onMessageChange}
      onHoneypotChange={onHoneypotChange}
      onRespond={onRespond}
      buttonsDisabled={buttonsDisabled}
      t={t}
    />
  );
}

interface AlreadyRespondedCardProps {
  response: import('@/api/types').ClientResponse;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function AlreadyRespondedCard({ response, locale, t }: Readonly<AlreadyRespondedCardProps>) {
  const isAccepted = response.response === 'accepted';
  const badgeClass = isAccepted
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('sharedQuote.alreadyResponded')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${badgeClass}`}>
          {isAccepted ? t('sharedQuote.accepted') : t('sharedQuote.rejected')}
        </span>
        <p className="text-sm text-muted-foreground">
          {t('sharedQuote.respondedBy', { name: response.name })}{' '}
          {t('sharedQuote.respondedOn', { date: formatDate(response.responded_at, locale) })}
        </p>
        {response.message && <p className="text-sm text-foreground italic">{response.message}</p>}
      </CardContent>
    </Card>
  );
}

interface ResponseFormProps {
  clientName: string;
  message: string;
  honeypot: string;
  onClientNameChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onHoneypotChange: (value: string) => void;
  onRespond: (response: 'accepted' | 'rejected') => void;
  buttonsDisabled: boolean;
  t: (key: string) => string;
}

function ResponseForm({
  clientName,
  message,
  honeypot,
  onClientNameChange,
  onMessageChange,
  onHoneypotChange,
  onRespond,
  buttonsDisabled,
  t,
}: Readonly<ResponseFormProps>) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-6 space-y-4">
        {/* Honeypot - hidden from real users */}
        <div className="hidden">
          <Input
            value={honeypot}
            onChange={(e) => onHoneypotChange(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
        </div>

        <div>
          <label htmlFor="response-name" className="text-sm font-medium">
            {t('sharedQuote.yourName')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="response-name"
            value={clientName}
            onChange={(e) => onClientNameChange(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="response-message" className="text-sm font-medium">
            {t('sharedQuote.message')}
          </label>
          <textarea
            id="response-message"
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm resize-none"
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => onRespond('accepted')}
            disabled={buttonsDisabled}
            className="flex-1 min-h-[44px] bg-green-600 hover:bg-green-700 text-white"
          >
            <Check className="mr-1 h-4 w-4" />
            {t('sharedQuote.accept')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => onRespond('rejected')}
            disabled={buttonsDisabled}
            className="flex-1 min-h-[44px]"
          >
            <X className="mr-1 h-4 w-4" />
            {t('sharedQuote.reject')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
